import { UtilityService } from './../../services/utility.service';
import { BehaviorSubject } from 'rxjs';
import { DialogService } from './../../services/dialog.service';
import { LookupService } from './../../services/lookup.service';
import { AuthService } from './../../services/auth.service';
import { Component, OnInit, Input, Output, forwardRef, ComponentFactoryResolver, ViewContainerRef, ViewChild, TemplateRef, EventEmitter, AfterViewInit, ElementRef, ChangeDetectionStrategy, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DropdownPosition, NgSelectComponent } from '@ng-select/ng-select';
import { LookupCustomConfig, LookupDTO } from '../../models/lookup.model';
import { ComplexLinqPredicateDTO, LinqPredicateDTO } from '../../models/linqPredicate.model';
import { DynamicLoaderDirectiveData } from '../dynamic-loader/dynamic-loader.directive';
import { UserDTO } from '../../models/user.model';
import { OverlayContainer } from '@angular/cdk/overlay';
import { GlobalService } from '../../services/global.service';

@Component({
  selector: 'eqp-lookup',
  templateUrl: './eqp-lookup.component.html',
  styleUrls: ['./eqp-lookup.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EqpLookupComponent),
      multi: true
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EqpLookupComponent implements OnInit, AfterViewInit, ControlValueAccessor {

  onChange: any = () => { }
  onTouch: any = () => { }
  val: any = null;
  interval: any = null;
  public currentUser: UserDTO;
  public globalService = GlobalService;

  @ViewChild('dialogDynamic', { static: true }) dialogDynamic: TemplateRef<any>;

  /**
   * Viechild per la gestione diretta del componente di select
   */
  @ViewChild("ngselect", { static: false }) ngselect: NgSelectComponent;

  modalAdding: MatDialogRef<TemplateRef<any>>;


  /**
   * Label che sarà visibile come etichetta della lookup
   */
  @Input("placeholder") placeholder: any;

  /**
   * Proprietà che sarà bindata sull'etichetta della lookup, default: 'label'
   */
  @Input("bindLabel") bindLabel: string;

  /**
   * Datasource della lookup
   */
  @Input("items") items: Array<any>;

  /**
   * Testo visualizzato in caso di risultati non trovati, se non specificato viene proposto un testo di default
   */
  @Input("notFoundText") notFoundText: string = 'Nessun risultato trovato';

  /**
   * Configurazione contenente il nome del componente, e gli eventuali parametri, da utilizzare
   * per la creazione di nuovi elementi dalla lookup
   */
  @Input("genericAddComponent") genericAddComponent: DynamicLoaderDirectiveData;

  /**
   * Definisce se la lookup accetta più valori, all'invio sul server sono gestiti come array di oggetti
   */
  @Input("isMultiple") isMultiple: boolean = false;

  /**
   * Definisce se la lookup accetta più valori, all'invio sul server sono gestiti come array di oggetti
   */
  @Input("clearSearchOnAdd") clearSearchOnAdd: boolean = false;

  /**
   * Definisce se è possibile digitare caratteri all'interno della lookup
   */
  @Input("isSearchable") isSearchable: boolean = true;

  /**
   * Definisce se è possibile mostrare il pulsante cancella nella lookup
   */
  @Input("isClearable") isClearable: boolean = true;

  /**
   * Rend er dinamico per una mole di dati elevata, utilizzare quando il datasource contiene molti elementi e risulta lento il caricamento dei dati
   */
  @Input("isVirtualScroll") isVirtualScroll: boolean = false;

  /**
   * Definisce se la lookup è solo in lettura
   */
  @Input("isReadonly") isReadonly: boolean = false;

  /**
   * Definisce se la lookup è obbligatorio che abbia un valore
   */
  @Input("isRequired") isRequired: boolean = false;

  /**
   * Indica se la modale in aggiunta avrà il pulsante di chiusura generico
   */
  @Input("isClosable") isClosable: boolean = false;

  @Input("isDisabled") isDisabled: boolean = false;

  /**
   * Nome dell'entità di tipo LookupDTO che sarà passato nel lookup.service/GetLookupEntities
   */
  @Input("entityType") entityType: string;

  @Input("namespaceClass") namespaceClass: string;

  /**
   * Scrivere in caso di utilizzo di formControlName, il nome del formGroup utilizzato nel tag <form>
   */
  @Input("formGroupInput") formGroupInput: any;

  // @Input("formControlIndex") formControlIndex: any;

  /**
   * Nome del formControlName da utilizzare
   */
  @Input("formControlNameInput") formControlNameInput: any;

  @Output("valueSelected") valueSelected: EventEmitter<LookupDTO> = new EventEmitter<LookupDTO>();

  /**
   * ngModel da bindare
   */
  @Input("ngModelInput") ngModelInput: any;

  @Input("isSearchWhileComposing") isSearchWhileComposing: boolean = false;

  @Input("bindCompleteObject") bindCompleteObject: boolean = true;

  /**
   * Input da utilizzare nel caso in cui la lookup è contenuta in un dialog o mat-tab
   * e si hanno problemi di visualizzazione del menù dropdown, passare come input la stringa 'body'
   */
  @Input("appendToInput") appendToInput: string;

  @Input("disableReloadOnLookupAddingCompleteParent") disableReloadOnLookupAddingCompleteParent: boolean = false;

  /**
   * Input per aggiungere opzioni tra quelle possibili nella lookup.
   * Per l'aggiunta vera e propria del dato dovrà essere gestito separatamente
   */
  @Input("addTag") addTag: boolean | ((term: string) => any | Promise<any>) = false;

  /**
   * Input per gestire il testo visualizzato quando addTag è true e si vuole aggiungere un elemento inline
   */
  @Input("addTagText") addTagText: string = "Add item";

  /**
   * Se true, rimanendo fermi sulle singole option sarà presente un tooltip con la Label intera della proprietà bindata
   */
  @Input("showOptionTooltip") showOptionTooltip: boolean = false;

  /**
   * Input per continuare a cercare tra le opzione della lookup a partire dalla label dell'oggetto selezionato
   */
  @Input("editableSearchTerm") editableSearchTerm: boolean;

  @Input("sortList") sortList: boolean = false;

  /**
   * Permette di definire (in maniera opzionale) gli eventuali predicati standard da applicare nel recuperare gli item per la lookup
    */
  @Input() dataFilter: Array<LinqPredicateDTO> = null;

  /**
   * Permette di definire (in maniera opzionale) le etichette da visualizzare nelle options della lookup
   */
  @Input() customConfig: LookupCustomConfig = null;

  /**
   * Permette di definire predicati complessi per la gestione di where part con condizioni complesse messe in OR tra loro
   * (Esempio: grup pi di condizioni che contengono sia AND che OR e che devono essere messe in OR tra loro)
   */
  @Input() complexDataFilter: Array<ComplexLinqPredicateDTO> = null;

  /**
  * Datasource iniziale per la lookup, da usare se si vuole bypassare la chiamata al LookupController
  */
  @Input("initialItems") initialItems: Array<LookupDTO> = null;

  @Input("ngModelOptions") ngModelOptions: any = null;

  @Input("isEditable") isEditable: boolean = false;

  @Input("isMultiline") isMultiline: boolean = false;
  @Input("offsetMultiline") offsetMultiline: number = 90;

  /**
   * Valori possibili auto, top e bottom
   */
  @Input("dropdownPosition") dropdownPosition: DropdownPosition = "auto";

  /**
   * Alla pressione del tab permette di selezionare l'elemento all'interno della lookup.
   * Utile per risolvere il problema dell'addTag (aggiungeva l'elemento solo alla pressione del tasto ENTER)
   */
  @Input("selectOnTab") selectOnTab: boolean = false;

  @Input("multiline") multiline: boolean = false;

  /**
   * Evento scatenato al cambiare del valore della select
   */
  @Output() ngModelInputChange: EventEmitter<any> = new EventEmitter<any>();

  @Output() lookupAddingCompleteParent: EventEmitter<any> = new EventEmitter<any>();

  @Output() searchChange: EventEmitter<any> = new EventEmitter<any>();

  @Output() keydown: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Evento scatenato al clear della select (tasto X)
   */
  @Output() clear: EventEmitter<any> = new EventEmitter<any>();

  private _data = new BehaviorSubject<any[]>([]);

  // change data to use getter and setter
  @Input()
  set data(value) {
    // set the latest value for _data BehaviorSubject
    this._data.next(value);
  };

  get data() {
    // get the latest value from _data BehaviorSubject
    return this._data.getValue();
  }

  guidEdit: string;
  entityID: number;
  class: string = "mat-form-field";

  constructor(
    private authService: AuthService,
    private lookupService: LookupService, 
    private dialog: MatDialog, 
    private overlayContainer: OverlayContainer,
    private elementRef: ElementRef) { }

  ngOnInit(): void {

    // if (this.formGroupInput != null && Array.isArray(this.formGroupInput.controls)) {
    //   this.formGroupInput = this.formGroupInput.controls[this.formControlIndex];
    // }

    //Se la lookup è obbligatoria aggiunge l'asterisco al placeholder
    if (this.isRequired == true)
      this.placeholder += " *";
    this.currentUser = this.authService.getCurrentUser();    

    this.reloadData();

    if (this.multiline == true)
      this.class = "mat-form-field multiline-lookup"
    

  }
  
  ngOnDestroy() {
    if (this.interval != null) {
      clearInterval(this.interval);
    }
  }

  ngAfterViewInit() {
    //D.S. Applico la darkmode runtime quando il dropdown viene creeato
    const isDarkMode = this.currentUser.IsDarkMode;
    //if (isDarkMode && this.ngselect.element.className=='mat-form-field multiline-lookup ng-select-taggable ng-select-searchable ng-select-clearable ng-select ng-select-single ng-untouched ng-pristine ng-valid') 
    if (isDarkMode && this.ngselect.placeholder=='') 
    {
      
    const originalOpen = this.ngselect.open;     
        originalOpen.call(this.ngselect);
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.addedNodes.length || mutation.removedNodes.length) {
              const options = document.querySelectorAll('.ng-dropdown-panel .ng-dropdown-panel-items .ng-option');
              options.forEach(option => {
                (option as HTMLElement).style.color = 'white'; // Testo bianco
                (option as HTMLElement).style.backgroundColor = '#393939'; // Sfondo nero
              });
            }
          });
        });
        observer.observe(document.body, { childList: true, subtree: true });

        this.ngselect.open = () => {
          originalOpen.call(this.ngselect);
          observer.observe(document.body, { childList: true, subtree: true });
        };

        this.ngselect.close = () => {
          this.ngselect.isOpen = false;
          observer.disconnect();
        };        
      };
  

    this.ngselect.handleArrowClick = function () {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
        this.filter("");
      }
    };
    // Se viene passato in input il componente di add, aggiungo il pulsante di add nell'ng-select
    var self = this;

    if (this.genericAddComponent) {
      //Salvo all'interno dell'entityID l'idLookupEntity che serve in caso di possibilità sia di aggiunta che di modifica
      this.entityID = this.genericAddComponent.idLookupEntity;

      //Il pulsante di ADD viene aggiunto solo SE:
      // - In input NON è presente nessun form group
      // - In input E' PRESENTE un form group e tale form group NON RISULTA essere disabilitato
      if (!this.isReadonly && (this.formGroupInput == null || this.formGroupInput == undefined || (this.formGroupInput != null && this.formGroupInput != undefined && this.formGroupInput.disabled != true))) {
        let guidAdd = Math.random().toString();
        var addButton = this.elementRef.nativeElement.querySelector('.ng-select-container');
        let tooltip = "Crea nuovo";//this.translate.instant("GENERIC_LABEL.LOOKUP_CREATE_NEW");
        addButton.insertAdjacentHTML('beforeend', '<span id="' + guidAdd + '" class="lookup-add-button ng-add-wrapper ng-star-inserted" title="' + tooltip + '" style="cursor:pointer;padding-left:5px;"><span aria-hidden="true" class= "ng-add" ><i class="fa fa-plus" aria-hidden="true"></i></span></span>');
        if (document.getElementById(guidAdd)) {
          document.getElementById(guidAdd).addEventListener('mousedown', function (e) {
            e.stopPropagation();
            self.genericAddComponent.idLookupEntity = 0;

            //Proprietà dell'ngSelect messa a false in quanto altrimenti rompe l'add di un articolo (nuovo oggetto bindato ma label non aggiornata)
            if (self.editableSearchTerm == true)
              self.ngselect.editableSearchTerm = false;
            self.openModalComponent();
          }, true)
        }

        //Gestione edit
        if (this.genericAddComponent.idLookupEntity != null) {
          this.addEditButton()
        }
      }

    }

    // if (this.isMultiline){
    //   var self = this;
    //   var container = this.elementRef.nativeElement.querySelector('.ng-select-container');
    //   container.classList.add("containerMultiline");

    //   var div = document.createElement("div");
    //   div.className = "lookupMultiline";

    //   div.onclick = () => {
    //     div.setAttribute("style","display: none");

    //     var input = this.elementRef.nativeElement.querySelector(".ng-select-container .ng-input input");
    //     input.setAttribute("style","display: block");
    //     input.focus();
    //     input.setSelectionRange(input.value.length,input.value.length);

    //     var ngLabel = container.querySelector(".ng-value-label");
    //     if (input.value == ""){
    //       if (ngLabel != null) ngLabel.setAttribute("style","display: block");
    //     } else {
    //       if (ngLabel != null) ngLabel.setAttribute("style","display: none");
    //     }

    //   }

    //   this.interval = setInterval(() => {
    //      if (this.ngModelInput != null){ div.innerHTML = this.ngModelInput.Label; }
    //   },500);

    //   container.querySelector(".ng-input").appendChild(div);
    //   container.querySelector(".ng-input").setAttribute('style','width: calc(100% - '+this.offsetMultiline+'px) !important');
    // }
  }

  // putDiv() {
  //   var ngContainer = this.elementRef.nativeElement.querySelector('.ng-select-container');
  //   if (ngContainer.querySelector('.lookupMultiline') != null) ngContainer.querySelector('.lookupMultiline').setAttribute("style", "display: block");
  //   if (ngContainer.querySelector('.ng-input input') != null) {
  //     ngContainer.querySelector(".ng-input input").setAttribute("style", "display: none");
  //     ngContainer.querySelector(".ng-value-label").setAttribute("style", "display: none");
  //   }


  // }

  // clearDiv() {
  //   this.elementRef.nativeElement.querySelector('.ng-select-container .lookupMultiline').innerHTML = "";
  // }

  addEditButton() {
    //guidEdit è uguale a null quando il pulsante di modifica non è nella lookup
    if (this.guidEdit == null && !this.isReadonly) {
      var self = this;
      this.guidEdit = Math.random().toString();
      var addButton = this.elementRef.nativeElement.querySelector('.ng-select-container');
      let tooltip = "Modifica";//this.translate.instant("GENERIC_LABEL.LOOKUP_CREATE_NEW");
      addButton.insertAdjacentHTML('beforeend', '<span id="' + this.guidEdit + '" class="lookup-edit-button ng-add-wrapper ng-star-inserted" title="' + tooltip + '" style="cursor:pointer;padding-left:5px;"><span aria-hidden="true" class= "ng-add" ><i class="fa fa-edit" aria-hidden="true"></i></span></span>');
      if (document.getElementById(this.guidEdit)) {
        document.getElementById(this.guidEdit).addEventListener('mousedown', function (e) {
          e.stopPropagation();
          self.genericAddComponent.idLookupEntity = self.entityID;

          //Proprietà dell'ngSelect messa a false in quanto altrimenti rompe l'add di un articolo (nuovo oggetto bindato ma label non aggiornata)
          if (self.editableSearchTerm == true)
            self.ngselect.editableSearchTerm = false;

          self.openModalComponent();
        }, true);
      }
    }
  }

  removeEditButton() {
    var editButton = document.getElementById(this.guidEdit);
    if (editButton != null)
      editButton.remove();

    this.guidEdit = null;
  }

  /**
   * Al change dei valori della select, aggiorno con l'emit il valore
   * @param event
   */
  onChangeSelect(event) {
    let realValue = null;
    if (this.bindCompleteObject == false)
      realValue = event != null ? (this.isMultiple == true ? event.map(l => l.ID) : event.ID) : null;
    else
      realValue = event;

    if (this.ngModelInputChange != null) {
      this.ngModelInputChange.emit(realValue);
      this.value = realValue;
      // this.putDiv(); //Cherri
    }
    if (this.genericAddComponent != null && this.genericAddComponent.isEditable == true) {
      if (event != null && event.ID != null) {
        this.entityID = event.ID;
        this.addEditButton();
      } else {
        this.removeEditButton();
      }
    }
  }

  async reloadData(selectedItemID = null) {
    if (this.entityType == null) {
      DialogService.Error("Non è stato possibile recuperare il nome dell'entità");
    }
    else if (this.initialItems != null && this.initialItems != undefined) {
      this.items = this.initialItems;
      //Se è stato richiesto di forzare la selezione a seguito del reload allora recupera l'elemento dato l'ID e lo valorizza nel controllo (questo caso si presenta quando dopo aver
      //aggiunto un nuovo elemento dalla lookup viene ricaricato il datasource e bisogna preselezionare la lookup con l'elemento appena aggiunto)
      if (selectedItemID != null && selectedItemID != undefined) {
        this.entityID = selectedItemID;
        // let newItems = await this.lookupService.GetLookupEntities(this.entityType, this.dataFilter, this.complexDataFilter, this.customConfig);
        // let newElement = newItems.find(x => x.ID == selectedItemID);
        // this.initialItems.push(newElement);
        // this.items = this.initialItems;

        let selectedValue = this.items.find(l => l.ID == selectedItemID);
        if (this.ngModelInputChange != null) {
          this.ngModelInputChange.emit(selectedValue);
          this.value = selectedValue;
        }

        //Proprietà editableSearchTerm dell'ngSelect bindata nuovamente con quella del componente.
        //In questo modo si evita il problema per cui in caso di aggiunta di un oggetto, viene effettuato il binding ma viene renderizzato con la label precedente
        
      }
    }
    else {
      this.lookupService.GetLookupEntities(this.entityType, this.dataFilter, this.complexDataFilter, this.customConfig, this.namespaceClass).then((res) => {
        if (this.sortList == true)
          res.sort(UtilityService.dynamicSort("Label"));

        this.items = res;

        //Se è stato richiesto di forzare la selezione a seguito del reload allora recupera l'elemento dato l'ID e lo valorizza nel controllo (questo caso si presenta quando dopo aver
        //aggiunto un nuovo elemento dalla lookup viene ricaricato il datasource e bisogna preselezionare la lookup con l'elemento appena aggiunto)
        if (selectedItemID != null && selectedItemID != undefined) {
          this.entityID = selectedItemID;
          let selectedValue = this.items.find(l => l.ID == selectedItemID);
          if (this.ngModelInputChange != null) {
            this.ngModelInputChange.emit(selectedValue);
            this.value = selectedValue;
          }
        }
        this.ngselect.editableSearchTerm = this.editableSearchTerm;
      })
    }
  }

  // Sezione two way bindings: Necessario per il funzionamento corretto del ControlValueAccessor (binding ngModel tra più componenti)
  set value(val) {
    this.val = val;
    // if (this.isMultiline) {
    //   this.elementRef.nativeElement.querySelector(".lookupMultiline").innerHTML = this.val.Label;
    // }

    this.onChange(val)
    this.onTouch(val)

    //Se è stato passato un evento di output allora lo invoca passando l'oggetto selezionato dalla lookup
    if (this.valueSelected)
      this.valueSelected.emit(this.val);
  }

  writeValue(value: any) {
    this.value = value
  }

  registerOnChange(fn: any) {
    this.onChange = fn
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn
  }
  // Fine sezione two way bindings

  openModalComponent() {
    let focusElement = <any>document.activeElement;
    focusElement.blur();

    this.ngselect.isOpen = false;
    //SUBSCRIBE PER RICARICARE I DATI DOPO IL SALVATAGGIO SOLTANTO SULLA LOOKUP CORRENTE
    if (this.lookupService.lookupAddingComplete.observers == null)
      this.lookupService.lookupAddingComplete = new EventEmitter<any>();

    this.lookupService.lookupAddingComplete.subscribe((res) => {
      this.lookupService.lookupAddingComplete.unsubscribe();

      //Riaggiorno l'ID del record appena modificato/inserito (solo nel caso in cui res sia diverso da null)
      //Res è uguale a null quando si chiude la modale con il pulsante "Annulla"
      this.entityID = res != null ? res : this.entityID;

      //Chiude la modale di aggiunta elemento
      if (this.modalAdding)
        this.modalAdding.close();

      //Se il parametro dell'emit è popolato correttamente con l'ID dell'elemento salvato allora ricarica il datasource della lookup e seleziona in automatico l'elemento appena aggiunto
      if (res != null && res != undefined && res > 0 && this.disableReloadOnLookupAddingCompleteParent != true)
        this.reloadData(res);

      this.lookupAddingCompleteParent.emit(res);
    });


    this.modalAdding = this.dialog.open(this.dialogDynamic,
      { disableClose: true, closeOnNavigation: false, minWidth: "70%", hasBackdrop: true }
    );

    this.modalAdding.afterClosed().subscribe(result => {
      this.ngselect.editableSearchTerm = this.editableSearchTerm;
    });
  }

  updateInitialItems(items: Array<LookupDTO>, selectedItemID = null) {
    this.initialItems = [...items];
    this.addEditButton();
    this.reloadData(selectedItemID);
  }

  /**
   * Richiamato allo svuotarsi della lookup tramite pulsante cancella
   * @param event
   */
  lookupCleared(event) {
    this.clear.emit(event);
  }

  searchChangeMethod(event) {
    this.searchChange.emit(event);
  }

  keydownEvent(event) {
    this.keydown.emit(event);
  }

  onBlur(event) {
    setTimeout(() => {
      if (this.editableSearchTerm == true)
        this.ngselect.editableSearchTerm = true;
    }, 100);
  }

  onFocus(event) {
    if (this.ngselect.selectedItems != null && this.ngselect.selectedItems.length > 0 && this.ngselect.filter != null)
      this.ngselect.filter(this.ngselect.selectedItems[0].label);

    let element: HTMLInputElement = <any>event.target;
    element.focus();
    element.setSelectionRange(element.value.length, element.value.length);

    setTimeout(() => {
      if (this.editableSearchTerm == true)
        this.ngselect.editableSearchTerm = false;
    }, 100);
  }
}
